import { z } from 'zod';

import { VerificationStrategySchema } from '../../auth/contact-verification/verification-strategy/verification-strategy';
import {
  IntegrationFieldDescriptorSchema,
  type IntegrationFieldDescriptor,
} from '../integration-field/integration-field';

/** Provider keys are lower-kebab slugs — they appear in URLs and in a DB column. */
const PROVIDER_KEY_PATTERN = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

/**
 * One provider of one capability, and everything needed to configure it.
 *
 * `wired: false` declares a provider the platform knows about but has no
 * adapter for yet (`smtp` at launch): staff can see it, it is never activated
 * silently, and its test-connection answers "probe not wired" rather than a
 * misleading success.
 *
 * `strategy` (P30b1) is carried by the providers of a code-bearing capability
 * only — `send`: the platform mints the code and the vendor carries the words;
 * `managed`: the vendor mints and checks the code itself. `devOnly` marks a
 * provider that exists for development (the api-log SMS vendor): activation is
 * refused outside `development | test`, and it is the one provider allowed to
 * declare no fields, because there is nothing to configure.
 */
export const IntegrationProviderDescriptorSchema = z
  .object({
    key: z
      .string()
      .regex(PROVIDER_KEY_PATTERN, 'A provider key is a lower-kebab slug, e.g. "revenuecat".'),
    wired: z.boolean(),
    fields: z.array(IntegrationFieldDescriptorSchema),
    strategy: VerificationStrategySchema.optional(),
    devOnly: z.boolean().optional(),
  })
  .strict()
  .refine((provider) => provider.fields.length > 0 || provider.devOnly === true, {
    message: 'A provider declares at least one field unless it is development-only.',
    path: ['fields'],
  })
  .refine(
    (provider) =>
      new Set(provider.fields.map((field) => field.key)).size === provider.fields.length,
    { message: 'Field keys must be unique within a provider.', path: ['fields'] },
  );

export type IntegrationProviderDescriptor = z.infer<typeof IntegrationProviderDescriptorSchema>;

/** The declared field, or `undefined` — an undeclared field is a 422, never a silent write. */
export function findProviderField(
  provider: IntegrationProviderDescriptor,
  fieldKey: string,
): IntegrationFieldDescriptor | undefined {
  return provider.fields.find((field) => field.key === fieldKey);
}

/** The fields that must resolve before this provider counts as configured. */
export function requiredFieldKeys(provider: IntegrationProviderDescriptor): string[] {
  return provider.fields.filter((field) => field.required).map((field) => field.key);
}

/** The fields that are encrypted at rest and never returned (N21). */
export function secretFieldKeys(provider: IntegrationProviderDescriptor): string[] {
  return provider.fields.filter((field) => field.secret).map((field) => field.key);
}
